if (!customElements.get('free-gift-selector')) {
  let freeGiftRequestInFlight = false;
  let freeGiftCorrectionTimer = null;

  class FreeGiftSelector extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', this.onOptionClick.bind(this));
    }

    connectedCallback() {
      clearTimeout(freeGiftCorrectionTimer);
      freeGiftCorrectionTimer = setTimeout(() => {
        const selector = document.querySelector('free-gift-selector');
        if (selector && selector.isConnected) selector.runAutoCorrection();
      }, ON_CHANGE_DEBOUNCE_TIMER);
    }

    runAutoCorrection() {
      if (freeGiftRequestInFlight) return;
      const giftLineKey = this.dataset.giftLineKey;
      if (!giftLineKey) return;
      const threshold = parseInt(this.dataset.threshold, 10);
      const required = parseInt(this.dataset.giftRequired, 10) || threshold;
      const eligibleTotal = parseInt(this.dataset.eligibleTotal, 10);
      const giftQuantity = parseInt(this.dataset.giftQuantity, 10) || 0;
      if (eligibleTotal < required) {
        this.changeGift(giftLineKey, 0);
      } else if (giftQuantity > 1) {
        this.changeGift(giftLineKey, 1);
      }
    }

    onOptionClick(event) {
      const option = event.target.closest('.free-gift__option');
      if (!option || freeGiftRequestInFlight) return;
      if (option.getAttribute('aria-pressed') === 'true') return;
      this.selectGift(parseInt(option.dataset.variantId, 10));
    }

    getSectionsToRender() {
      const cartComponent = this.closest('cart-drawer-items') || this.closest('cart-items');
      return cartComponent ? cartComponent.getSectionsToRender() : [];
    }

    async selectGift(variantId) {
      freeGiftRequestInFlight = true;
      this.setLoading(true);
      const sections = this.getSectionsToRender();
      try {
        const previousKey = this.dataset.giftLineKey;
        if (previousKey) {
          await fetch(routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({ id: previousKey, quantity: 0 }),
          });
        }
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }],
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();
        if (parsedState.status) {
          this.showError(parsedState.description || window.cartStrings.error);
          return;
        }
        this.renderSections(parsedState, sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: parsedState });
      } catch (error) {
        console.error(error);
        this.showError(window.cartStrings.error);
      } finally {
        freeGiftRequestInFlight = false;
        this.setLoading(false);
      }
    }

    async changeGift(lineKey, quantity) {
      freeGiftRequestInFlight = true;
      const sections = this.getSectionsToRender();
      try {
        const response = await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: lineKey,
            quantity,
            sections: sections.map((section) => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();
        if (parsedState.errors) return;
        this.renderSections(parsedState, sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-gift', cartData: parsedState });
      } catch (error) {
        console.error(error);
      } finally {
        freeGiftRequestInFlight = false;
      }
    }

    renderSections(parsedState, sections) {
      if (!parsedState.sections) return;
      sections.forEach((section) => {
        const sectionElement = document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections[section.section]) return;
        const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    setLoading(isLoading) {
      this.querySelectorAll('.free-gift__option').forEach((option) => {
        option.toggleAttribute('disabled', isLoading);
      });
    }

    showError(message) {
      const errorElement = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (errorElement) errorElement.textContent = message;
    }
  }

  customElements.define('free-gift-selector', FreeGiftSelector);
}

if (!customElements.get('free-gift-bar')) {
  class FreeGiftBar extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.free-gift__open-cart')) return;
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else {
          window.location.href = routes.cart_url;
        }
      });
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this.abortController) this.abortController.abort();
      clearTimeout(this.refreshTimer);
    }

    onCartUpdate() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), ON_CHANGE_DEBOUNCE_TIMER);
    }

    refresh() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const freshBar = html.getElementById(this.id);
          if (freshBar) this.replaceWith(freshBar);
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.error(error);
        });
    }
  }

  customElements.define('free-gift-bar', FreeGiftBar);
}
